import { db } from '../../../../firebase/config'
import { client } from '../../../../hooks/Client'
import softDeleteDoc from '../../../../helpers/softDeleteDoc'
import softDeleteDocs from '../../../../helpers/softDeleteDocs'

/**
 * @typedef {import('../../../../types/LiveReports2').LiveReports2Doc} LiveReports2Doc
 */

/**
 * Of een widget in de editor is weggehaald en dus door `cleanupDeletedWidgets`
 * definitief opgeruimd mag worden.
 *
 * De editor zet alleen `Deleted` (via de serializer). Een widget die met zijn
 * rapportage de prullenbak in ging draagt ook `DeletedAt` — die moet blijven
 * staan, anders is het rapport alsnog leeg zodra iemand de oude link opent.
 *
 * @param {{ Deleted?: boolean, DeletedAt?: any }} widget - Widgetdocument uit Firestore
 * @returns {boolean}
 */
export const isEditorRemovedWidget = (widget) => Boolean(widget?.Deleted) && !widget?.DeletedAt

/**
 * Zet een rapportage mét zijn widgets in de prullenbak (ADR-008).
 *
 * Nodig naast `softDeleteDocs` omdat die geen weet heeft van de widgets van een
 * rapportage. De widgets worden geregistreerd met `ParentID` = de docid van de
 * rapportage: `restoreDeletedItem` zet ze dan mee terug, en de prullenbak toont
 * alleen de rapportage (LiveReportWidgets staat bewust niet in TRASH_COLLECTIONS).
 *
 * Widgets die al `Deleted` zijn, zijn in de editor weggehaald en blijven buiten
 * de cascade — anders kwamen ze bij terugzetten weer tevoorschijn.
 *
 * Eerst de rapportage, dan de widgets: mislukt de tweede stap, dan staat de
 * rapportage in de prullenbak met zijn widgets nog intact, en terugzetten werkt.
 * Fouten gaan door naar de aanroeper.
 * AI-CONTEXT: See docs/pitfalls.md#database-hard-to-soft-delete-leaves-old-orphans
 *
 * @param {object} params
 * @param {LiveReports2Doc} params.report - De rapportage (met `ID` en `docid`)
 * @param {string} [params.authId] - Wie het verwijdert
 * @returns {Promise<void>}
 */
const softDeleteLiveReport = async ({ report, authId }) => {
    const title = report.Name || 'Rapportage'

    await softDeleteDoc({ collection: 'LiveReports2', docid: report.docid, authId, title })

    // Own tenant only — rules require CompagnyID or IsTemplate filter.
    const widgetsSnapshot = await db.collection('LiveReportWidgets')
        .where('ReportId', '==', report.ID)
        .where('CompagnyID', '==', client)
        .get()

    const docids = widgetsSnapshot.docs
        .filter((doc) => !doc.data().Deleted)
        .map((doc) => doc.id)

    await softDeleteDocs({ collection: 'LiveReportWidgets', docids, authId, title, parentId: report.docid })
}

export default softDeleteLiveReport
